import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import cx from 'classnames';
import classes from './TierCard.scss';
import MoveIn from '../columns/Actions/actions/MoveIn';
import Reservation from '../columns/Actions/actions/Reservation';
import Call from '../columns/Actions/actions/Call';
import RentalShortForm from '../../../../experiments/RentalShortForm';
import ActionButtonsTierPricing from '../columns/Actions/ActionButtonsTierPricing';
import SelectedUnitModal from '../SelectedUnitModal';
import mixpanel from '../../../../../src/web/utils/mixpanel';
import useCurrentPage from '../../../../hooks/useCurrentPage';
import { Price } from '../columns';
import ApplicableFees from '../../ApplicableFees';

export const addWebsiteQueryParam = (url) => {
  if (!url) { return ''; }

  const urlObject = new URL(url);

  // Check if 'fromWebsite' query parameter already exists
  if (!urlObject.searchParams.has('fromWebsite')) {
    urlObject.searchParams.append('fromWebsite', 'true');
  }

  return urlObject.toString();
};

export const addMixpanelQueryParam = (url, mixpanelDistinctId) => {
  if (!url) {
    return '';
  }

  return url.replace(/companyId=/, `mpid=${mixpanelDistinctId}&companyId=`);
};


const TierCard = ({ moveInUrl,
  reservationUrl,
  activeButton,
  actionButtons,
  toggleTiers,
  tier,
  enableTieredPricing,
  feeFilter,
  ...rest }) => {
  const [fullMoveInUrl, setFullMoveInUrl] = useState(addWebsiteQueryParam(moveInUrl));
  const [fullReserveUrl, setFullReserveUrl] = useState(addWebsiteQueryParam(reservationUrl));
  const [showSelectedUnitModal, setShowSelectedUnitModal] = useState(false);
  const [showRentalShortFormModal, setShowRentalShortFormModal] = useState(false);

  const closeSelectedUnitModal = () => setShowSelectedUnitModal(false);
  const {
    primaryColorClass,
    secondaryColorBackgroundClass,
    primaryColorBackgroundHoverClass,
    buttonStyleClass,
  } = useCurrentPage();

  const [trackAction, setTrackAction] = useState(null);
  const { setTierSelected } = rest;

  useEffect(() => {
    if (trackAction && !setTierSelected) {
      setTierSelected(prevState => ({
        ...prevState,
        tierSelected: tier?.name,
        priceSelected: tier?.price,
        actionSelected: trackAction,
      }));
    }
  }, [trackAction]);

  const toggleSelectedUnitModal = () => {
    setShowSelectedUnitModal(!showSelectedUnitModal);
    setTrackAction('selectButton');
  };

  const toggleRentalShortFormModal = () => setShowRentalShortFormModal(!showRentalShortFormModal);

  // We can only get the mixpanel distinct ID on the client side.
  useEffect(() => {
    const mixpanelDistinctId = mixpanel.getDistinctId();

    if (mixpanelDistinctId) {
      setFullMoveInUrl(addMixpanelQueryParam(fullMoveInUrl, mixpanelDistinctId));
      setFullReserveUrl(addMixpanelQueryParam(fullReserveUrl, mixpanelDistinctId));
    }
  }, []);

  const { facility, priceDisplay } = rest;
  const callActionThreshold = facility?.settings?.callActionThreshold;
  const showCallButton = tier?.availableUnitsCount < (callActionThreshold || 1);
  let actionButtonContent = '';

  const newFeeFilter = {
    ...feeFilter,
    unitTierId: tier?.tierId || null,
  };


  if (activeButton === 'moveIn') {
    actionButtonContent = (
      <MoveIn
        {...rest}
        moveInUrl={addWebsiteQueryParam(moveInUrl)}
        text="Select"
        styleMoveInUG={classes.moveInButton}
        tier={tier}
        setTrackAction={setTrackAction}
      />
    );
  } else if (activeButton === 'reserve') {
    actionButtonContent = (
      <Reservation
        {...rest}
        reservationUrl={addWebsiteQueryParam(reservationUrl)}
        text="Select"
        styleReservationUG={classes.reserveButton}
        enableTieredPricing={enableTieredPricing}
        setTrackAction={setTrackAction}
        {...tier}
      />
    );
  } else if (activeButton === 'rentalShortForm') {
    actionButtonContent = (
      <RentalShortForm
        closeSelectedUnitModal={closeSelectedUnitModal}
        inModalContext={showSelectedUnitModal}
        moveInText={actionButtons.moveIn}
        reserveText={actionButtons.reserve}
        reserveUrl={reservationUrl}
        moveInUrl={moveInUrl}
        unitGroupId={rest.id}
        showRentalShortFormModal={showRentalShortFormModal}
        toggleRentalShortFormModal={toggleRentalShortFormModal}
        setTrackAction={setTrackAction}
        startText="Select"
        tier={tier}
        showCallButton={showCallButton}
        {...rest}
      />
    );
  } else if (activeButton === 'actionArrow') {
    actionButtonContent = (
      <Button
        block
        className={cx(
          secondaryColorBackgroundClass,
          primaryColorBackgroundHoverClass,
          buttonStyleClass,
          classes.reserveButton,
        )}
        onClick={toggleSelectedUnitModal}
      >
        Select
      </Button>
    );
  }
  if (showCallButton) {
    actionButtonContent = (
      <Call
        {...rest}
        price={tier?.price}
        discountedPrice={tier.discountedPrice}
        text={actionButtons.call}
        useTel={activeButton === 'actionArrow'}
        setTrackAction={setTrackAction}
        styleCallUG={classes.callButton}
      />
    );
  }
  return (
    <div className={classes.tierCard}>
      <div className={classes.tierPricingGroup}>
        <div className={classes.tieredPricingText}>
          <div className={classes.subContainer}>
            <div className={cx(primaryColorClass, classes.textContainer)}>
            {tier?.name.length > 20 ? `${tier?.name.substring(0, 20)}...` : tier?.name}
            </div>
            <Price
              facility={facility}
              priceDisplay={priceDisplay}
              price={tier?.price}
              discountedPrice={tier?.discountedPrice}
              renderPriceInTier
            />
            <ApplicableFees facility={facility} feeFilter={newFeeFilter} />
          </div>
          <div className={classes.subContainer}>
            <div className={classes.unitInfo}>
              {tier?.description?.length > 100 ? `${tier?.description?.substring(0, 100)}...` : tier?.description}
            </div>
          </div>
        </div>
        {actionButtonContent}
        {showSelectedUnitModal && (
          <SelectedUnitModal
            {...rest}
            price={tier?.price}
            discountedPrice={tier.discountedPrice}
            handleClose={closeSelectedUnitModal}
          >
            <ActionButtonsTierPricing
              {...rest}
              actionButtons={actionButtons}
              closeSelectedUnitModal={closeSelectedUnitModal}
              inModalContext
              moveInUrl={fullMoveInUrl}
              reservationUrl={fullReserveUrl}
              showRentalShortFormModal={showRentalShortFormModal}
              toggleRentalShortFormModal={toggleRentalShortFormModal}
              toggleTiers={toggleTiers}
              tier={tier}
            />
          </SelectedUnitModal>
        )}
        {/* When the GET STARTED button is clicked in the underlying RentalShortForm code, the
        SelectedUnitModal will disappear, causing the underlying code to disappear as well.
        To handle this, we have framed the underlying code without the SelectedUnitModal,
        which will be restored when clicked.  */}
        {!showSelectedUnitModal && showRentalShortFormModal && (
          (
            <ActionButtonsTierPricing
              {...rest}
              actionButtons={actionButtons}
              closeSelectedUnitModal={closeSelectedUnitModal}
              inModalContext
              moveInUrl={fullMoveInUrl}
              reservationUrl={fullReserveUrl}
              showRentalShortFormModal={showRentalShortFormModal}
              inTierCard
              toggleRentalShortFormModal={toggleRentalShortFormModal}
              toggleTiers={toggleTiers}
              tier={tier}
            />
          )
        )}
      </div>
    </div>
  );
};

export default TierCard;
